import React, { useState, useEffect } from 'react';
import { X, Minus, Plus, AlertCircle, Trash2, CheckCircle2, Truck } from 'lucide-react';
import { SEED_PRODUCTS } from '../../../../../services/api/warehouseApi';
import { getAllProducts } from '../../../../../services/api/productApi';

const LoadingRequestRevisionModal = ({ isOpen, request, onClose, onSubmitRevision }) => {
    const [items, setItems] = useState(() => {
        if (!request?.items) return [];
        return request.items.map(item => ({
            ...item,
            revisedQty: item.requestedQty ?? item.warehouseQty ?? 0
        }));
    });
    const [reason, setReason] = useState('');
    const [showAddItemModal, setShowAddItemModal] = useState(false);
    const [availableProducts, setAvailableProducts] = useState(SEED_PRODUCTS);
    const [selectedNewProduct, setSelectedNewProduct] = useState('');
    const [newProductQty, setNewProductQty] = useState(10);
    const [submittedSuccess, setSubmittedSuccess] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        let active = true;
        const loadProducts = async () => {
            try {
                const res = await getAllProducts({ size: 1000, status: 'ACTIVE' });
                if (!active) return;
                const fetched = (res.content || res.products || res || []).map(p => ({
                    id: p.id || p.publicId,
                    name: p.name,
                    sku: p.sku || '',
                    unit: p.unit || p.uom || 'Pcs'
                }));
                if (fetched.length > 0) setAvailableProducts(fetched);
            } catch (e) {}
        };
        loadProducts();
        return () => { active = false; };
    }, [isOpen]);

    if (!isOpen || !request) return null;

    const handleQtyChange = (productId, newQty) => {
        setItems(prev => prev.map(item => {
            if (item.productId === productId) {
                // allow raw value to prevent stuck 0
                return { ...item, revisedQty: newQty };
            }
            return item;
        }));
    };

    const handleIncrement = (productId) => {
        setItems(prev => prev.map(item => {
            if (item.productId === productId) {
                const current = Number(item.revisedQty) || 0;
                return { ...item, revisedQty: current + 1 };
            }
            return item;
        }));
    };

    const handleDecrement = (productId) => {
        setItems(prev => prev.map(item => {
            if (item.productId === productId) {
                const current = Number(item.revisedQty) || 0;
                return { ...item, revisedQty: Math.max(0, current - 1) };
            }
            return item;
        }));
    };

    const handleRemoveItem = (productId) => {
        setItems(prev => prev.filter(i => i.productId !== productId));
    };

    const handleAddNewItem = () => {
        if (!selectedNewProduct) return;
        const prod = availableProducts.find(p => p.id === selectedNewProduct);
        if (!prod) return;

        const existing = items.find(i => i.productId === prod.id);
        if (existing) {
            setItems(prev => prev.map(i => i.productId === prod.id ? { ...i, revisedQty: (Number(i.revisedQty) || 0) + Number(newProductQty) } : i));
        } else {
            setItems(prev => [
                ...prev,
                {
                    id: `rev_i_${Date.now()}`,
                    productId: prod.id,
                    productName: prod.name,
                    suggestedQty: 0,
                    requestedQty: 0,
                    revisedQty: Number(newProductQty),
                    unit: prod.unit,
                    source: 'Added Item'
                }
            ]);
        }
        setShowAddItemModal(false);
        setSelectedNewProduct('');
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!reason.trim()) {
            alert('Please enter a reason for requesting this revision.');
            return;
        }

        const formattedItems = items.map(i => ({
            ...i,
            requestedQty: Number(i.revisedQty) || 0,
            warehouseQty: Number(i.revisedQty) || 0
        }));

        onSubmitRevision({
            requestId: request.id,
            items: formattedItems,
            reason: reason.trim(),
            salesperson: request.salesperson
        });

        setSubmittedSuccess(true);
    };

    if (submittedSuccess) {
        return (
            <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
                <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-100 dark:border-slate-700 shadow-2xl w-full max-w-md p-6 text-center space-y-5 animate-in fade-in zoom-in duration-200">
                    <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center mx-auto">
                        <CheckCircle2 className="w-10 h-10" />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-slate-800 dark:text-white">Revision Submitted!</h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                            Revision of <span className="font-bold text-slate-800 dark:text-white">{request.requestNo}</span> has been sent to warehouse for review.
                        </p>
                    </div>

                    <div className="p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-2xl text-left text-xs">
                        <div className="text-slate-500 font-semibold mb-1">Status: <span className="text-amber-600 font-bold">Revision Pending</span></div>
                        <div className="text-slate-500">Reason: {reason}</div>
                    </div>

                    <div className="flex items-center gap-3 pt-2">
                        <button
                            onClick={onClose}
                            className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-500/20"
                        >
                            Back to Requests
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-100 dark:border-slate-700 shadow-2xl w-full max-w-2xl overflow-hidden my-8">
                
                {/* Header */}
                <div className="p-4 sm:p-6 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between bg-slate-50/50 dark:bg-slate-800">
                    <div className="min-w-0 flex-1 mr-2">
                        <h2 className="text-base sm:text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2 flex-wrap">
                            Request Revision <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300">({request.requestNo})</span>
                        </h2>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 text-xs text-slate-500 dark:text-slate-400 mt-1">
                            <span className="inline-flex items-center gap-1 font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/40 px-2 py-0.5 rounded-md border border-blue-100 dark:border-blue-800/60 w-fit text-[11px] sm:bg-transparent sm:dark:bg-transparent sm:border-0 sm:p-0 sm:text-xs sm:font-semibold sm:text-slate-700 sm:dark:text-slate-300">
                                <Truck className="w-3 h-3 text-blue-500 shrink-0" />
                                <span>Vehicle: {request.vehicle || 'Van 01'}</span>
                            </span>
                            <span className="hidden sm:inline text-slate-300 dark:text-slate-600">|</span>
                            <span className="truncate">
                                Warehouse: <strong className="font-semibold text-slate-700 dark:text-slate-300">{request.warehouse || 'Main Warehouse'}</strong>
                            </span>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-full shrink-0">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
                    {/* Alert Banner */}
                    <div className="p-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-2xl flex items-start gap-3 text-xs text-amber-800 dark:text-amber-300">
                        <AlertCircle className="w-5 h-5 flex-shrink-0 text-amber-600 mt-0.5" />
                        <div>
                            <strong className="font-semibold block mb-0.5">Approved Request Revision</strong>
                            You are requesting changes to an approved Loading Request. Warehouse staff will review and compare your requested changes before updating the load.
                        </div>
                    </div>

                    {/* Table of items with stepper */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-slate-800 dark:text-white text-xs uppercase tracking-wider">Modify Items</h3>
                            <button
                                type="button"
                                onClick={() => setShowAddItemModal(true)}
                                className="inline-flex items-center gap-1 text-xs font-bold text-blue-600 dark:text-blue-400 hover:underline"
                            >
                                + Add New Item
                            </button>
                        </div>

                        <div className="border border-slate-100 dark:border-slate-700 rounded-2xl overflow-hidden">
                            <table className="w-full text-left text-xs">
                                <thead className="bg-slate-50 dark:bg-slate-700/50 text-slate-500 font-semibold border-b border-slate-100 dark:border-slate-700">
                                    <tr>
                                        <th className="p-3 pl-4">Product</th>
                                        <th className="p-3 text-center">Current Qty</th>
                                        <th className="p-3 text-center">Revised Qty</th>
                                        <th className="p-3 text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                    {items.map((item) => {
                                        const originalQty = item.requestedQty ?? item.warehouseQty ?? 0;
                                        const currentVal = item.revisedQty;
                                        const numericVal = Number(currentVal) || 0;
                                        const diff = numericVal - originalQty;

                                        return (
                                            <tr key={item.productId} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/30">
                                                <td className="p-3 pl-4 font-semibold text-slate-800 dark:text-white">
                                                    {item.productName}
                                                    {diff !== 0 && (
                                                        <span className={`ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                                                            diff > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                                                        }`}>
                                                            {diff > 0 ? `+${diff}` : diff}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="p-3 text-center font-medium text-slate-500">
                                                    {originalQty} <span className="text-[10px] text-slate-400">{item.unit || 'Pcs'}</span>
                                                </td>
                                                <td className="p-3 text-center">
                                                    <div className="inline-flex items-center gap-1.5 bg-slate-100 dark:bg-slate-700/80 p-1.5 rounded-xl border border-slate-200/60 dark:border-slate-600/60">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDecrement(item.productId)}
                                                            className="w-8 h-8 flex items-center justify-center bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-200 active:scale-95 shadow-xs transition-all shrink-0 cursor-pointer"
                                                        >
                                                            <Minus className="w-4 h-4" />
                                                        </button>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={currentVal}
                                                            onChange={(e) => handleQtyChange(item.productId, e.target.value)}
                                                            className="w-24 sm:w-28 text-center bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 font-extrabold text-sm sm:text-base py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => handleIncrement(item.productId)}
                                                            className="w-8 h-8 flex items-center justify-center bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:scale-95 shadow-xs transition-all shrink-0 cursor-pointer"
                                                        >
                                                            <Plus className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="p-3 text-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveItem(item.productId)}
                                                        className="p-1.5 text-slate-400 hover:text-rose-600 rounded-lg hover:bg-rose-50 transition-colors"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Reason input (Required) */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                            Reason for Revision <span className="text-rose-500">*</span>
                        </label>
                        <textarea
                            required
                            rows={3}
                            placeholder="e.g. Received additional customer orders or need extra route stock..."
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className="w-full px-3.5 py-2.5 text-xs bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 text-slate-800 dark:text-white"
                        />
                    </div>

                    {/* Footer Controls */}
                    <div className="flex items-center justify-end gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2.5 text-xs font-semibold text-slate-600 dark:text-slate-400 hover:text-slate-800"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-500/20"
                        >
                            Submit Revision
                        </button>
                    </div>
                </form>

                {/* Add Extra Item Sub-Modal */}
                {showAddItemModal && (
                    <div className="fixed inset-0 z-[10000] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-100 dark:border-slate-700 shadow-xl w-full max-w-sm space-y-4">
                            <h3 className="font-bold text-sm text-slate-800 dark:text-white">Add Extra Item to Revision</h3>
                            
                            <div>
                                <label className="block text-[11px] font-semibold text-slate-500 mb-1">Select Product</label>
                                <select
                                    value={selectedNewProduct}
                                    onChange={(e) => setSelectedNewProduct(e.target.value)}
                                    className="w-full px-3 py-2 text-xs bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 text-slate-800 dark:text-white"
                                >
                                    <option value="">-- Choose Product --</option>
                                    {availableProducts.map(p => (
                                        <option key={p.id} value={p.id}>{p.name} {p.sku ? `(${p.sku})` : ''}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-[11px] font-semibold text-slate-500 mb-1">Quantity</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={newProductQty}
                                    onChange={(e) => setNewProductQty(e.target.value)}
                                    className="w-full px-3 py-2 text-xs bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 text-slate-800 dark:text-white font-bold"
                                />
                            </div>

                            <div className="flex justify-end gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowAddItemModal(false)}
                                    className="px-3 py-2 text-xs text-slate-600"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleAddNewItem}
                                    disabled={!selectedNewProduct}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-xl text-xs font-bold disabled:opacity-50"
                                >
                                    Add Item
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LoadingRequestRevisionModal;
